import React from 'react';
import { styled } from '@linaria/react';
import { animated, useSpring } from 'react-spring';

import { normalize } from '@/utils';

import Demo, { DemoSliderControl } from '@/components/Demo';
import Paper from '@/components/Paper';
import ToggleButton from '@/components/ToggleButton';

import { CODE_BACKGROUND } from './index.constants';

function FixedPopup({ variant, codeOutput }) {
  const [isMarginRevealed, setIsMarginRevealed] =
    React.useState(false);

  return (
    <Demo
      initialValues={{
        containerWidth: 100,
        containerHeight: 100,
      }}
      controlPosition="top"
      customGrid={{ gridTemplateColumns: '1fr 1fr' }}
      outputWrapper={null}
      controls={[
        <DemoSliderControl
          key="containerWidth"
          id="containerWidth"
          label="Container Width"
          min={30}
          max={100}
          previewRenderer={(val) => `${val}%`}
        />,
        <DemoSliderControl
          key="containerHeight"
          id="containerHeight"
          label="Container Height"
          min={30}
          max={100}
          previewRenderer={(val) => `${val}%`}
        />,
      ]}
    >
      {({ containerWidth, containerHeight }) => {
        const containerHeightPx = normalize(
          containerHeight,
          30,
          100,
          175,
          // The code snippet gets slightly taller when we add the annotation
          variant === 'highlighted' ? 287 : 282
        );
        return (
          <div>
            <Row>
              <Column>{codeOutput}</Column>
              <DemoColumn>
                <Container
                  style={{
                    width: containerWidth + '%',
                    height: containerHeightPx / 16 + 'rem',
                  }}
                >
                  <Background>
                    <Heading>
                      {[3, 5, 1, 4, 7].map((width, index) => (
                        <HeadingBlock
                          key={index}
                          style={{ width: `${width}rem` }}
                        />
                      ))}
                    </Heading>
                    <Paragraph>
                      {[
                        1, 4, 1.5, 2, 4.75, 0.5, 4, 7, 1.25, 1.5,
                        2.25, 1, 5, 0.5, 2.1, 0.785, 2.5, 4.2, 3.5, 1,
                      ].map((width, index) => (
                        <Block
                          key={index}
                          style={{ width: `${width}rem` }}
                        />
                      ))}
                    </Paragraph>
                    <Paragraph>
                      {[
                        0.785, 4.2, 1.25, 4, 1.5, 1.5, 1, 2.25, 0.5,
                        2.1, 2.5, 3.5, 1,
                      ].map((width, index) => (
                        <Block
                          key={index}
                          style={{ width: `${width}rem` }}
                        />
                      ))}
                    </Paragraph>
                  </Background>
                  <MarginBoxes
                    containerWidth={containerWidth}
                    containerHeight={containerHeight}
                    isMarginRevealed={isMarginRevealed}
                  />
                  <Modal />
                </Container>
              </DemoColumn>
            </Row>
            {variant === 'highlighted' && (
              <Footer>
                <ToggleButton
                  active={isMarginRevealed}
                  handleToggle={() =>
                    setIsMarginRevealed(!isMarginRevealed)
                  }
                >
                  Reveal Margin
                </ToggleButton>
              </Footer>
            )}
          </div>
        );
      }}
    </Demo>
  );
}

function MarginBoxes({
  containerWidth,
  containerHeight,
  isMarginRevealed,
}) {
  // The spring settings should depend on the container width; essentially, the smaller the margin is, the quicker the animation should run.
  // We also need to lengthen the animation for the "left" variant, since there's only 1 margin which is 2x the size.
  const widthConfig = {
    tension: 200,
    friction: normalize(containerWidth, 30, 100, 10, 30),
  };
  const heightConfig = {
    tension: 200,
    friction: normalize(containerHeight, 30, 100, 16, 40),
  };
  const rightMarginStyles = useSpring({
    clipPath: isMarginRevealed
      ? `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)`
      : `polygon(100% 0%, 100% 0%, 100% 100%, 100% 100%)`,
    config: widthConfig,
  });
  const leftMarginStyles = useSpring({
    clipPath: isMarginRevealed
      ? `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)`
      : `polygon(0% 0%, 0% 0%, 0% 100%, 0% 100%)`,
    config: widthConfig,
  });
  const topMarginStyles = useSpring({
    clipPath: isMarginRevealed
      ? `polygon(0% 0%, 100% 0%, 100% 100%, 0% 100%)`
      : `polygon(0% 0%, 100% 0%, 100% 0%, 0% 0%)`,
    config: heightConfig,
  });
  const bottomMarginStyles = useSpring({
    clipPath: isMarginRevealed
      ? `polygon(0% 100%, 100% 100%, 100% 0%, 0% 0%)`
      : `polygon(0% 100%, 100% 100%, 100% 100%, 0% 100%)`,
    config: heightConfig,
  });

  return (
    <>
      <HorizontalMarginWrapper>
        <HorizontalMargin style={leftMarginStyles} />
        <PlaceholderModal style={{ opacity: 0 }}>
          .element
        </PlaceholderModal>
        <HorizontalMargin style={rightMarginStyles} />
      </HorizontalMarginWrapper>
      <VerticalMarginWrapper>
        <VerticalMargin style={topMarginStyles} />
        <PlaceholderModal style={{ opacity: 0 }}>
          .element
        </PlaceholderModal>
        <VerticalMargin style={bottomMarginStyles} />
      </VerticalMarginWrapper>
    </>
  );
}

const Row = styled.div`
  display: flex;
  gap: 24px;
  padding-bottom: 24px;

  @media (max-width: 34rem) {
    flex-direction: column-reverse;
  }
`;
const Column = styled.div`
  position: relative;
  flex: 1;
  background: ${CODE_BACKGROUND};
  border-radius: 8px;
  min-height: 230px;

  @media (max-width: 34rem) {
    min-height: revert;
  }
`;

const DemoColumn = styled(Column)`
  display: flex;
  justify-content: center;
`;

const Container = styled(Paper)`
  position: relative;
  background: hsl(0deg 0% 90%);
  color-scheme: light;
  overflow: hidden;
  overflow: clip;
`;

const Background = styled.div`
  overflow: hidden;
  overflow: clip;
  padding: 16px;

  max-height: 100%;
`;

const Heading = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 1.5em;
`;
const Paragraph = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 1em;
`;

const Block = styled.span`
  background: hsl(0deg 0% 80%);
  border-radius: 2px;
  height: 0.75rem;
`;
const HeadingBlock = styled(Block)`
  height: 1rem;
  background: hsl(0deg 0% 70%);
`;

const PlaceholderModal = styled.div`
  width: 175px;
  height: 100px;
  max-width: 100%;
`;

const Modal = styled(PlaceholderModal)`
  position: absolute;
  inset: 0;
  margin: auto;
  background: white;
  box-shadow:
    0px 1px 2px hsl(0deg 0% 0% / 0.2),
    0px 2px 4px hsl(0deg 0% 0% / 0.2),
    0px 4px 8px hsl(0deg 0% 0% / 0.2);
  border-radius: 2px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: center;
`;

const MarginWrapper = styled.div`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
`;
const HorizontalMarginWrapper = styled(MarginWrapper)`
  flex-direction: row;
`;
const VerticalMarginWrapper = styled(MarginWrapper)`
  flex-direction: column;
`;

const Margin = styled(animated.div)`
  flex: 1;
  background: hsl(30deg 100% 70% / 0.5);
`;

const HorizontalMargin = styled(Margin)`
  height: 100px;
`;
const VerticalMargin = styled(Margin)`
  width: 175px;
`;

export default FixedPopup;
